import { useState, useMemo, useCallback } from 'react';
import GalleryNavbar from './components/GalleryNavbar';
import GalleryGrid from './components/GalleryGrid';
import Lightbox from './components/Lightbox';
import UploadModal from './components/UploadModal';
import { mockGalleryImages } from '../../mocks/galleryImages';

const categories = ['전체', '주거 공간', '상업 공간', '리모델링'];
const ADMIN_PASSWORD = '4747';
const DELETED_IDS_KEY = 'gallery_deleted_ids';
const UPLOADED_IMAGES_KEY = 'gallery_uploaded_images';

function getDeletedIds(): string[] {
  try {
    const raw = localStorage.getItem(DELETED_IDS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function getUploadedImages(): typeof mockGalleryImages {
  try {
    const raw = localStorage.getItem(UPLOADED_IMAGES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function buildImageList() {
  const deletedIds = new Set(getDeletedIds());
  const uploaded = getUploadedImages();
  const base = mockGalleryImages.filter((img) => !deletedIds.has(img.id));
  return [...uploaded.filter((img) => !deletedIds.has(img.id)), ...base];
}

export default function GalleryPage() {
  const [images, setImages] = useState(buildImageList);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [activeCategory, setActiveCategory] = useState('전체');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const filteredImages = useMemo(() => {
    if (activeCategory === '전체') return images;
    return images.filter((img) => img.category === activeCategory);
  }, [images, activeCategory]);

  const handleDelete = useCallback((id: string) => {
    const deletedIds = getDeletedIds();
    if (!deletedIds.includes(id)) {
      deletedIds.push(id);
      localStorage.setItem(DELETED_IDS_KEY, JSON.stringify(deletedIds));
    }
    // Also remove from uploaded images if it was an uploaded one
    const uploaded = getUploadedImages();
    const updatedUploaded = uploaded.filter((img) => img.id !== id);
    localStorage.setItem(UPLOADED_IMAGES_KEY, JSON.stringify(updatedUploaded));

    setImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  const handleUpload = useCallback((files: { url: string; name: string }[], category: string) => {
    const newImages = files.map((file, index) => ({
      id: `img-upload-${Date.now()}-${index}`,
      url: file.url,
      title: file.name.replace(/\.[^/.]+$/, ''),
      date: new Date().toISOString().slice(0, 10).replace(/-/g, '.'),
      category,
    }));

    const uploaded = getUploadedImages();
    const updatedUploaded = [...newImages, ...uploaded];
    localStorage.setItem(UPLOADED_IMAGES_KEY, JSON.stringify(updatedUploaded));

    setImages((prev) => [...newImages, ...prev]);
  }, []);

  const handleAdminLogin = () => {
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAdmin(true);
      setShowPasswordPrompt(false);
      setPasswordInput('');
    } else {
      alert('비밀번호가 올바르지 않습니다.');
      setPasswordInput('');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <GalleryNavbar />

      {/* Hero Header */}
      <section className="pt-12 pb-8 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="text-xs tracking-widest text-gray-400 mb-3 uppercase">갤러리</p>
            <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight font-serif">
              프로젝트 사진
            </h1>
            <p className="text-base text-gray-500 mt-3 max-w-lg">
              <span className="font-bold">P</span>.collective의 다양한 프로젝트 사진을 감상해보세요.
            </p>
          </div>
          
          {/* 관리자 로그인 버튼 */}
          {!isAdmin && (
            <button
              onClick={() => setShowPasswordPrompt(true)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-lock-line mr-2"></i>
              관리자 모드
            </button>
          )}
          
          {isAdmin && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-5 py-2.5 text-sm font-medium text-white bg-[#D97757] rounded-lg hover:bg-[#C86647] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-add-line text-base"></i>
                사진 추가
              </button>
              <span className="text-sm text-green-600 font-medium">
                <i className="ri-shield-check-line mr-1"></i>
                관리자
              </span>
              <button
                onClick={() => setIsAdmin(false)}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                로그아웃
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Filter Bar */}
      <section className="px-6 lg:px-12 max-w-7xl mx-auto mb-10">
        <div className="flex items-center gap-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
              }`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto text-sm text-gray-400">
            {filteredImages.length}장의 사진
          </span>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="px-6 lg:px-12 max-w-7xl mx-auto pb-24">
        <GalleryGrid
          images={filteredImages}
          onImageClick={(index) => setLightboxIndex(index)}
          onDelete={isAdmin ? handleDelete : undefined}
        />
      </section>

      {/* Lightbox */}
      <Lightbox
        images={filteredImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
        onNavigate={setLightboxIndex}
        onDelete={isAdmin ? handleDelete : undefined}
      />

      {/* 사진 업로드 모달 */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
      />

      {/* 비밀번호 입력 모달 */}
      {showPasswordPrompt && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => {
            setShowPasswordPrompt(false);
            setPasswordInput('');
          }}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">관리자 인증</h3>
              <p className="text-sm text-gray-500 mt-1">비밀번호를 입력해주세요</p>
            </div>
            
            <div className="px-8 py-6">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                placeholder="비밀번호"
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                autoFocus
              />
            </div>
            
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowPasswordPrompt(false);
                  setPasswordInput('');
                }}
                className="px-6 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={handleAdminLogin}
                className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#2A2A2A] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-400">© 2024 <span className="font-bold">P</span>.collective. All rights reserved.</p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
